import { Box, Switch, Tooltip, Typography } from '@mui/material';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';
import { labelSx, sliderSwitchSx } from './constraintStyles';

/**
 * Header row shared by the Inside / Outside sections: a label (i18n id) and a
 * toggle switch styled to match the toolbar's slider row.
 */
export function ConstraintToggleRow({
    labelId,
    tooltipId,
    checked,
    disabled = false,
    onToggle,
}: {
    labelId: string;
    /** Optional i18n id for a label tooltip explaining the modifier. */
    tooltipId?: string;
    checked: boolean;
    /** The switch cannot be operated; its value is kept. */
    disabled?: boolean;
    onToggle: (enabled: boolean) => void;
}) {
    const intl = useIntl();
    const labelText = intl.formatMessage({ id: labelId });
    const label = (
        <Typography sx={labelSx}>
            <FormattedMessage id={labelId} />
        </Typography>
    );
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
            }}
        >
            {tooltipId ? (
                <Tooltip
                    title={intl.formatMessage({ id: tooltipId })}
                    placement="left"
                >
                    {label}
                </Tooltip>
            ) : (
                label
            )}
            <Switch
                disableRipple
                sx={sliderSwitchSx}
                checked={checked}
                disabled={disabled}
                inputProps={{ 'aria-label': labelText }}
                onChange={(_, isChecked) => onToggle(isChecked)}
            />
        </Box>
    );
}

/**
 * Amber warning marker whose tooltip (an i18n message id) explains why a
 * constraint is inactive or unavailable. Kept at full opacity so it stays
 * prominent even when the surrounding row is dimmed.
 */
export function ConstraintWarningIcon({
    titleId,
    placement = 'top',
}: {
    titleId: string;
    placement?: 'top' | 'right' | 'bottom' | 'left';
}) {
    const intl = useIntl();
    return (
        <Tooltip
            title={intl.formatMessage({ id: titleId })}
            placement={placement}
        >
            <WarningAmberIcon
                sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }}
            />
        </Tooltip>
    );
}
